import { useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { usePostalCity, formatCp } from "@/lib/postal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const CIVIL = [["solteiro", "Solteiro(a)"], ["casado", "Casado(a)"], ["divorciado", "Divorciado(a)"], ["viuvo", "Viúvo(a)"], ["uniao_facto", "União de facto"]];
export const REGIME = [["adquiridos", "Comunhão de adquiridos"], ["separacao", "Separação de bens"], ["geral", "Comunhão geral"]];
const LABEL = Object.fromEntries([...CIVIL, ...REGIME, ["nao_sei", "Regime por confirmar"]]);
const inp = "w-full rounded-lg border border-slate-200 px-3 py-2 text-[15px]";

export function Chips({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
          className={`px-3.5 py-2.5 rounded-xl border-2 text-sm font-medium transition-colors ${value === k ? "bg-ec-green border-ec-green text-white" : "border-slate-200 text-slate-700 bg-white"}`}>{l}</button>
      ))}
    </div>
  );
}

const lines = (b) => [
  b.civil_status && [LABEL[b.civil_status], b.regime && LABEL[b.regime]].filter(Boolean).join(" · "),
  b.spouse_name && `Cônjuge: ${b.spouse_name}`,
  b.address && [b.address, [b.postal_code, b.locality].filter(Boolean).join(" ")].filter(Boolean).join(", "),
].filter(Boolean);

// Características de cada pessoa (ligam documentos e perguntas do KYC); o texto do estrangeiro depende do lado.
const TRAITS = [["company", "É uma empresa", "Empresa", () => "certidão permanente do registo comercial, RCBE e identificação do representante"],
  ["foreign", "É estrangeiro / não residente", "Estrangeiro", (side) => (side === "sellers"
    ? "passaporte, NIF português, prova de morada no estrangeiro, representante fiscal ou adesão às notificações eletrónicas, procuração se não vier à escritura e IBAN"
    : "passaporte, NIF português e prova de morada no estrangeiro")],
  ["proxy", "Representado por procurador", "Procurador", () => "procuração e identificação do procurador"]];
// Documentos a pedir também (não são características da pessoa).
const EXTRA_DOCS = [["buyer_deliberacao", "Deliberação da empresa", "se os estatutos exigirem", "buyer_company"],
  ["credit_letter", "Carta de aprovação do crédito", "condição no CPCV", "has_credit"], ["buyer_funds", "Origem dos fundos", "Branqueamento de capitais"]];

function useToggle(p, onDone) {
  const [busy, setBusy] = useState(null);
  const toggle = async (k) => {
    setBusy(k);
    try { await api.put(`/processes/${p.id}`, { [k]: !p[k] }); onDone(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(null);
  };
  return [busy, toggle];
}

const Sw = ({ on, label, hint, onClick, disabled, testid }) => (
  <button type="button" role="switch" aria-checked={!!on} disabled={disabled} onClick={onClick} title={hint} data-testid={testid}
    className="flex items-center gap-2 text-sm text-slate-700 disabled:opacity-60">
    <span className={`relative w-8 h-[18px] rounded-full border transition-colors ${on ? "bg-ec-green border-ec-green" : "bg-slate-200 border-slate-300"}`}>
      <span className={`absolute left-0 top-[1px] w-[14px] h-[14px] rounded-full bg-white shadow transition-transform ${on ? "translate-x-[15px]" : "translate-x-[1px]"}`} />
    </span>{label}
  </button>
);

// Documentos do comprador a pedir também (topo do grupo do comprador em Documentos).
export function BuyerAsks({ p, onDone, ro, testid = "buyer-asks" }) {
  const [busy, toggle] = useToggle(p, onDone);
  if (p.stage === "angariacao" || !(p.buyers || []).length) return null;
  const more = EXTRA_DOCS.filter(([, , , dep]) => !dep || p[dep]);
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500" data-testid={testid}>Pedir também:
      {more.map(([k, l, hint]) => (
        <button key={k} type="button" disabled={ro || busy === k} onClick={() => toggle(k)} title={hint} data-testid={`${testid}-${k}`} aria-pressed={!!p[k]}
          className={`px-2.5 py-1 rounded-full border font-medium transition-colors ${p[k] ? "bg-ec-green border-ec-green text-white" : "border-slate-200 text-slate-600 bg-white hover:border-slate-400"}`}>{l}</button>
      ))}
    </div>
  );
}

export const traitsOf = (p, side, idx) => p.party_traits?.[side]?.[idx] || {};

// No ✎ de cada pessoa (controlado; grava com «Guardar»).
export function TraitFields({ value, onChange, side, idx, ro }) {
  return (
    <div className="space-y-2.5" data-testid={`traits-${side}-${idx}`}>
      {TRAITS.map(([k, l, , hint]) => (
        <div key={k}>
          <Sw on={value[k]} label={l} disabled={ro} onClick={() => onChange({ ...value, [k]: !value[k] })} testid={`trait-${side}-${idx}-${k}`} />
          <p className="text-xs text-slate-400 ml-10" data-testid={`trait-${side}-${idx}-${k}-hint`}>Pede {hint(side)}.</p>
        </div>
      ))}
    </div>
  );
}

export const TraitTags = ({ p, side, idx }) => TRAITS.filter(([k]) => traitsOf(p, side, idx)[k]).map(([k, , tag]) => (
  <span key={k} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px] font-medium" data-testid={`party-trait-${side}-${idx}-${k}`}>{tag}</span>
));

// Ficha do comprador: dados para o CPCV, com «preenchido pelo cliente» e correção pelo consultor.
export function BuyerCpcvData({ p, idx, onDone, ro }) {
  const b = (p.buyers || [])[idx] || {};
  const [open, setOpen] = useState(false);
  const ls = lines(b);
  if (!ls.length) return null;
  return (
    <div className="mt-1.5 text-sm text-slate-600 space-y-0.5" data-testid={`buyer-cpcv-${idx}`}>
      {ls.map((l) => <div key={l} className="break-words">{l}</div>)}
      <div className="flex items-center gap-2 text-xs">
        {b.client_filled_at && <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-800" data-testid={`buyer-client-filled-${idx}`}>preenchido pelo cliente</span>}
        {!ro && <button onClick={() => setOpen(true)} className="text-ec-green underline underline-offset-2" data-testid={`buyer-cpcv-edit-${idx}`}>Corrigir</button>}
      </div>
      {open && <BuyerDataDialog p={p} idx={idx} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone(); }} />}
    </div>
  );
}

function BuyerDataDialog({ p, idx, onClose, onDone }) {
  const b = p.buyers[idx];
  const [f, setF] = useState({ civil_status: b.civil_status || "", regime: b.regime || "", spouse_name: b.spouse_name || "", address: b.address || "",
    postal_code: b.postal_code || "", locality: b.locality || "", email: b.email || "", phone: b.phone || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((o) => ({ ...o, [k]: typeof v === "string" ? v : v.target.value }));
  usePostalCity(f.postal_code, f.locality, set("locality"));
  const submit = async () => {
    setBusy(true);
    try { await api.put(`/processes/${p.id}/buyers/${idx}/cpcv-data`, f); toast.success("Dados corrigidos"); onDone(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="buyer-cpcv-dialog">
        <DialogHeader><DialogTitle className="font-heading navy-text">Dados para o CPCV · {b.display_name || b.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Chips options={CIVIL} value={f.civil_status} onChange={set("civil_status")} testid="bd-civil" />
          {["casado", "uniao_facto"].includes(f.civil_status) && <Chips options={REGIME} value={f.regime} onChange={set("regime")} testid="bd-regime" />}
          {f.civil_status === "casado" && <input className={inp} placeholder="Nome do cônjuge" value={f.spouse_name} onChange={set("spouse_name")} data-testid="bd-spouse" />}
          <input className={inp} placeholder="Morada" value={f.address} onChange={set("address")} data-testid="bd-address" />
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <input className={inp} placeholder="0000-000" inputMode="numeric" value={f.postal_code} onChange={(e) => set("postal_code")(formatCp(e.target.value))} data-testid="bd-cp" />
            <input className={inp} placeholder="Localidade" value={f.locality} onChange={set("locality")} data-testid="bd-locality" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input className={inp} placeholder="Email" value={f.email} onChange={set("email")} data-testid="bd-email" />
            <input className={inp} placeholder="Telemóvel" value={f.phone} onChange={set("phone")} data-testid="bd-phone" />
          </div>
          <button onClick={submit} disabled={busy} className="w-full h-10 rounded-lg bg-ec-green text-white font-semibold disabled:opacity-50" data-testid="bd-save">Guardar</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
